import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useBodyClass } from "../../helper";

const PREVIEW_MAX_WIDTH = 1920;

function ImagePreviewOverlay({ files, initialIndex, onClose }) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [imageLoaded, setImageLoaded] = useState(false);

  useBodyClass(["modal-open"]);

  const currentFile = files[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === files.length - 1;

  const goNext = useCallback(() => {
    if (currentIndex < files.length - 1) {
      setImageLoaded(false);
      setCurrentIndex((i) => i + 1);
    }
  }, [currentIndex, files.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setImageLoaded(false);
      setCurrentIndex((i) => i - 1);
    }
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, goPrev, goNext]);

  const handleBackdropClick = () => onClose();
  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1055,
        background: "rgba(0, 0, 0, 0.9)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={handleBackdropClick}
    >
      {/* Close button */}
      <button
        type="button"
        className="btn btn-link text-white"
        style={{ position: "absolute", top: 16, right: 16, fontSize: "1.5rem" }}
        onClick={stopPropagation}
        onClickCapture={onClose}
        aria-label={t("common.close")}
      >
        <i className="fa fa-xmark" />
      </button>

      {/* Previous button */}
      {!isFirst && (
        <button
          type="button"
          className="btn btn-link text-white"
          style={{
            position: "absolute",
            left: 16,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "2rem",
          }}
          onClick={stopPropagation}
          onClickCapture={goPrev}
          aria-label={t("common.previous")}
        >
          <i className="fa fa-chevron-left" />
        </button>
      )}

      {/* Next button */}
      {!isLast && (
        <button
          type="button"
          className="btn btn-link text-white"
          style={{
            position: "absolute",
            right: 16,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "2rem",
          }}
          onClick={stopPropagation}
          onClickCapture={goNext}
          aria-label={t("common.next")}
        >
          <i className="fa fa-chevron-right" />
        </button>
      )}

      {/* Loading spinner */}
      {!imageLoaded && (
        <div
          className="text-white"
          style={{ position: "absolute", fontSize: "2rem" }}
        >
          <i className="fa fa-spinner fa-spin" />
        </div>
      )}

      {/* Image */}
      <img
        src={`/v1/files/${currentFile.id}/thumbnails?width=${PREVIEW_MAX_WIDTH}`}
        alt={currentFile.name || ""}
        style={{
          maxWidth: "90vw",
          maxHeight: "85vh",
          objectFit: "contain",
          opacity: imageLoaded ? 1 : 0,
          transition: "opacity 0.2s",
        }}
        onClick={stopPropagation}
        onLoad={() => setImageLoaded(true)}
      />

      {/* Counter + filename */}
      <div
        className="text-white text-center mt-3"
        style={{ position: "absolute", bottom: 24 }}
        onClick={stopPropagation}
      >
        {currentIndex + 1} / {files.length}
        {currentFile.name && (
          <span className="ms-2 text-white-50">{currentFile.name}</span>
        )}
      </div>
    </div>
  );
}

ImagePreviewOverlay.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
    })
  ).isRequired,
  initialIndex: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ImagePreviewOverlay;
